import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { BREAKPOINTS } from "../stitches.config";

export type ScreenSize = "mobile" | "tablet" | "desktop";

export const useWindowSize = () => {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useLayoutEffect(() => {
    const updateSize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return size;
};

export const useIsKeyboardOpen = () => {
  //if the document height is less than the window height the virtual keyboard is open:
  //https://twitter.com/rikschennink/status/1361598959828037633
  return useDocumentHeight() < window.innerHeight;
};

const useDocumentHeight = () => {
  const getHeight = useCallback(
    () =>
      window.visualViewport ? window.visualViewport.height : window.innerHeight,
    [],
  );
  const [height, setHeight] = useState<number>(getHeight());

  useEffect(() => {
    const handleResize = () => {
      setHeight(getHeight());
    };

    //call handleResize on resize and orientationchange events
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [getHeight]);

  return height;
};

export const useIsScreenSize = (
  ...targetScreenSizes: ScreenSize[]
): boolean => {
  const { width } = useWindowSize();
  const actualScreenSize = useMemo(() => {
    if (width < BREAKPOINTS.MD) return "mobile";
    if (width < BREAKPOINTS.LG) return "tablet";
    return "desktop";
  }, [width]);
  return targetScreenSizes.includes(actualScreenSize);
};
